import React, { CSSProperties, Suspense, lazy, useState } from "react";
import { Routes, Route } from "react-router-dom";
import { Spinner, SpinnerSize } from "@fluentui/react";
import "./App.css";
import { useFetchExtensionFolders } from "./Hooks/AdoHooks/UseFetchExtensionFolders";
import { AppProvider } from "./Contexts";
import { useGetFeatureParam } from "./Hooks/UseFeature";
import { AcceptedFeatures } from "./Shared/shared.consts";
import { SessionProvider } from "./Contexts/SessionContext";
import { useCheckRepoAccess } from "./Hooks/AdoHooks/UseCheckRepoAccess";

const EV2Experiments = lazy(() => import("./Shared/EV2Experiments"));
const ExtensionDetails = lazy(
  () => import("./Experiment/ExtensionDetails/ExtensionDetails"),
);
const TopBar = lazy(() => import("./Layout/TopBar"));
const ADOAccessErrorMessageBar = lazy(
  () => import("./Shared/ADOAccessErrorMessageBar"),
);
const ErrorMessageBar = lazy(() =>
  import("./Shared/ErrorMessageBar").then((module) => ({
    default: module.ErrorMessageBar,
  })),
);

const loadingContainerStyle: CSSProperties = {
  alignItems: "center",
  display: "flex",
  height: "100%",
  justifyContent: "center",
};

const App: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const hideTopBar = useGetFeatureParam(AcceptedFeatures.hideTopBar.key);

  const { hasAccess, loading: loadingAccess } = useCheckRepoAccess();

  const { value: extensions = [], loading } =
    useFetchExtensionFolders(setError);

  const fallback = (
    <div style={loadingContainerStyle}>
      <Spinner label="Loading extensions..." size={SpinnerSize.large} />
    </div>
  );

  if (loading || loadingAccess) {
    return fallback;
  }

  return (
    <AppProvider initialExtensions={extensions} extensionError={error}>
      <div className="App" data-testid="app-container">
        <Suspense>
          {!hideTopBar && <TopBar extensions={extensions} />}
          <ErrorMessageBar />
          {!hasAccess && <ADOAccessErrorMessageBar />}
        </Suspense>
        <Suspense fallback={fallback}>
          <Routes>
            <Route
              path="/extensions/:extensionName/experiment/:experimentName/clone"
              element={<EV2Experiments isClone={true} />}
            />
            <Route
              path="/extensions/:extensionName/experiment/create"
              element={<EV2Experiments isCreate={true} />}
            />
            <Route
              path="/extensions/:extensionName/experiment/:experimentName"
              element={<EV2Experiments />}
            />
            <Route
              path="/extensions/:extensionName"
              element={<EV2Experiments />}
            />
            <Route
              path="/extensions"
              element={<EV2Experiments isRoot={true} />}
            />
            <Route
              path="/extensions/:extensionName/extensionDetails"
              element={
                <SessionProvider>
                  <ExtensionDetails />
                </SessionProvider>
              }
            />
            <Route path="/" element={<EV2Experiments isRoot={true} />} />
          </Routes>
        </Suspense>
      </div>
    </AppProvider>
  );
};

export default App;
